import React, { forwardRef } from 'react';

import AvatarHead, { HEAD_LAYOUTS } from './AvatarHead';
import AvatarExpression from './AvatarExpression';
import AvatarHair from './AvatarHair';
import AvatarClothing from './AvatarClothing';

import { getAvatarOption } from '../data/avatarOptions';

import {
  defaultAvatar,
  normalizeAvatarConfig,
} from '../data/avatarDefaults';

const AvatarRenderer = forwardRef(function AvatarRenderer({
  avatar = defaultAvatar,
  size = 320,
  className = '',
  label = 'BarterFi avatar preview',
  viewBox = '0 0 400 400',
  showBackground = true,
  rounded = true,
}, ref) {

  const config = normalizeAvatarConfig(avatar);

  const character = getAvatarOption(
    'characterType',
    config.characterType
  );

  const head = getAvatarOption('head', config.head);
  const skin = getAvatarOption('skinTone', config.skinTone);

  const expression = getAvatarOption(
    'expression',
    config.expression
  );


  const eyeShape = getAvatarOption(
    'eyeShape',
    config.eyeShape
  );

  const eyeColor = getAvatarOption(
    'eyeColor',
    config.eyeColor
  );

  const hairStyle = getAvatarOption('hair', config.hair);
  const hairPalette = getAvatarOption(
    'hairColor',
    config.hairColor
  );

  const clothingStyle = getAvatarOption(
    'clothing',
    config.clothing
  );

  const clothingPalette = getAvatarOption(
    'clothingColor',
    config.clothingColor
  );

  const background = getAvatarOption(
    'background',
    config.background
  );

  // Preserve style IDs while applying the selected colors.
  const hair = {
    ...hairStyle,
    color: hairPalette.color,
    highlight: hairPalette.highlight,
  };

  const clothing = {
    ...clothingStyle,
    color: clothingPalette.color,
    shadow: clothingPalette.shadow,
    highlight: clothingPalette.highlight,
  };

  const characterType = character.id;
  const layout = HEAD_LAYOUTS[head.id] || HEAD_LAYOUTS.circle;

  const hairProps = {
    characterType,
    hair,
    headShape: head.id,
    centerX: layout.centerX,
    hairY: layout.hairY,
  };

  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      preserveAspectRatio="xMidYMax meet"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={label}
      focusable="false"
      style={{
        display: 'block',
        maxWidth: '100%',
        height: 'auto',
      }}
    >
      <svg
        x="0"
        y="0"
        width="400"
        height="400"
        viewBox="0 0 400 400"
        overflow="hidden"
        style={{ borderRadius: rounded ? 48 : 0 }}
      >
        {showBackground && (
          <rect
            width="400"
            height="400"
            fill={background.color}
          />
        )}

        {/* Long hair behind the head and shoulders. */}
        <AvatarHair {...hairProps} layer="back" />

        <AvatarHead
          characterType={characterType}
          shape={head.id}
          skin={skin}
        />

        <AvatarClothing
          characterType={characterType}
          clothing={clothing}
          skin={skin}
          layout={layout}
        />

        <AvatarExpression
          expression={expression}
          characterType={characterType}
          skin={skin}
          layout={layout}
          eyeShape={eyeShape}
          eyeColor={eyeColor}
        />


        {/* Fringes and braids sit in front of the character. */}
        <AvatarHair {...hairProps} layer="front" />
      </svg>
    </svg>
  );

});

export default AvatarRenderer;
